// Gatilhos do fluxo e a fila de precedência — Fase 11.
//
// Duas metades, e a segunda é a que evita o bug caro:
//
//  1. Os gatilhos DESTE fluxo — quando ele começa sozinho.
//  2. A FILA DA ORGANIZAÇÃO INTEIRA, na ordem em que o motor avalia. Sem ela,
//     dois fluxos com gatilhos parecidos viram uma disputa invisível: um deles
//     nunca dispara e ninguém consegue explicar por quê. A lista vem da view
//     `inbox_gatilhos_ordem`, que usa a MESMA cláusula de ordenação da função
//     que decide — não é uma reprodução da regra, é a regra.

import React, { useCallback, useEffect, useState } from 'react';
import { AlertTriangle, ArrowDown, Loader2, Plus, Trash2, X, Zap } from 'lucide-react';
import type { Gatilho, GatilhoComFluxo, TipoGatilho } from '../apiGatilhos';
import {
  PRECEDENCIA, ROTULO_GATILHO, apagarGatilho, carregarGatilhos, carregarGatilhosDaOrg, salvarGatilho,
} from '../apiGatilhos';

interface Props {
  flowId: string;
  orgId: string | null;
  onFechar: () => void;
}

const EVENTOS = [
  'mensagem.recebida',
  'ticket.aberto',
  'ticket.resolvido',
  'conversa.atribuida',
];

const rotuloClasse = 'block text-[11px] font-semibold uppercase tracking-wide text-gray-500 dark:text-slate-400';
const campoClasse =
  'mt-1 w-full rounded-lg border border-gray-200 bg-white px-2.5 py-1.5 text-sm text-gray-800 outline-none focus:border-primary dark:border-slate-600 dark:bg-slate-700 dark:text-slate-100';

/** Config em branco de cada tipo, com o formato que o SQL espera. */
function configPadrao(tipo: TipoGatilho): Record<string, unknown> {
  switch (tipo) {
    case 'palavra_chave': return { palavras: [] };
    case 'fora_do_horario': return { inicio: '08:00', fim: '18:00', dias: [1, 2, 3, 4, 5] };
    case 'evento': return { evento: 'ticket.aberto' };
    case 'sem_resposta': return { minutos: 60 };
    default: return {};
  }
}

export default function PainelGatilhos({ flowId, orgId, onFechar }: Props) {
  const [gatilhos, setGatilhos] = useState<Gatilho[]>([]);
  const [fila, setFila] = useState<GatilhoComFluxo[]>([]);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState<string | null>(null);
  const [salvandoId, setSalvandoId] = useState<string | null>(null);

  const buscar = useCallback(async () => {
    try {
      const [g, f] = await Promise.all([
        carregarGatilhos(flowId),
        orgId ? carregarGatilhosDaOrg(orgId) : Promise.resolve([]),
      ]);
      setGatilhos(g);
      setFila(f);
    } catch (e) {
      setErro((e as Error).message);
    } finally {
      setCarregando(false);
    }
  }, [flowId, orgId]);

  useEffect(() => { void buscar(); }, [buscar]);

  const salvar = useCallback(async (g: Gatilho) => {
    setSalvandoId(g.id);
    setErro(null);
    try {
      await salvarGatilho(g);
      await buscar();
    } catch (e) {
      setErro((e as Error).message);
    } finally {
      setSalvandoId(null);
    }
  }, [buscar]);

  const adicionar = useCallback(async (tipo: TipoGatilho) => {
    setSalvandoId('novo');
    setErro(null);
    try {
      await salvarGatilho({
        flow_id: flowId,
        tipo,
        config: configPadrao(tipo),
        // 100 é o meio da escala de propósito: dá espaço para colocar algo
        // antes (menor) sem renumerar tudo.
        prioridade: 100,
        ativo: true,
      });
      await buscar();
    } catch (e) {
      setErro((e as Error).message);
    } finally {
      setSalvandoId(null);
    }
  }, [buscar, flowId]);

  const remover = useCallback(async (id: string) => {
    setSalvandoId(id);
    try {
      await apagarGatilho(id);
      await buscar();
    } catch (e) {
      setErro((e as Error).message);
    } finally {
      setSalvandoId(null);
    }
  }, [buscar]);

  const editarConfig = (g: Gatilho) => {
    const cfg = g.config ?? {};

    if (g.tipo === 'palavra_chave') {
      const palavras = Array.isArray(cfg.palavras) ? (cfg.palavras as string[]) : [];
      return (
        <div>
          <label className={rotuloClasse}>Palavras (uma por linha)</label>
          <textarea
            rows={3}
            defaultValue={palavras.join('\n')}
            onBlur={(e) =>
              void salvar({
                ...g,
                config: {
                  ...cfg,
                  palavras: e.target.value.split('\n').map((p) => p.trim()).filter(Boolean),
                },
              })
            }
            placeholder={'orçamento\nsegunda via'}
            className={`${campoClasse} resize-y`}
          />
          <p className="mt-1 text-[11px] leading-snug text-gray-400 dark:text-slate-500">
            Acento e maiúscula não importam. Lista vazia não dispara nada — é de propósito.
          </p>
        </div>
      );
    }

    if (g.tipo === 'fora_do_horario') {
      const dias = Array.isArray(cfg.dias) ? (cfg.dias as number[]) : [1, 2, 3, 4, 5];
      return (
        <div className="space-y-2">
          <div className="flex gap-2">
            <div className="flex-1">
              <label className={rotuloClasse}>Abre</label>
              <input
                type="time"
                defaultValue={String(cfg.inicio ?? '08:00')}
                onBlur={(e) => void salvar({ ...g, config: { ...cfg, inicio: e.target.value } })}
                className={campoClasse}
              />
            </div>
            <div className="flex-1">
              <label className={rotuloClasse}>Fecha</label>
              <input
                type="time"
                defaultValue={String(cfg.fim ?? '18:00')}
                onBlur={(e) => void salvar({ ...g, config: { ...cfg, fim: e.target.value } })}
                className={campoClasse}
              />
            </div>
          </div>
          <div>
            <label className={rotuloClasse}>Dias de atendimento</label>
            <div className="mt-1 flex gap-1">
              {['seg', 'ter', 'qua', 'qui', 'sex', 'sáb', 'dom'].map((d, i) => {
                const numero = i + 1; // isodow: segunda = 1
                const marcado = dias.includes(numero);
                return (
                  <button
                    key={d}
                    onClick={() =>
                      void salvar({
                        ...g,
                        config: {
                          ...cfg,
                          dias: marcado ? dias.filter((x) => x !== numero) : [...dias, numero].sort(),
                        },
                      })
                    }
                    className={`flex-1 rounded-md py-1 text-[10px] font-semibold transition-colors ${
                      marcado
                        ? 'bg-primary text-white'
                        : 'bg-gray-100 text-gray-400 dark:bg-slate-700 dark:text-slate-500'
                    }`}
                  >
                    {d}
                  </button>
                );
              })}
            </div>
            <p className="mt-1 text-[11px] leading-snug text-gray-400 dark:text-slate-500">
              Dia desmarcado conta como fora do horário o dia inteiro. Horário de Brasília.
            </p>
          </div>
        </div>
      );
    }

    if (g.tipo === 'evento') {
      return (
        <div>
          <label className={rotuloClasse}>Evento</label>
          <select
            value={String(cfg.evento ?? '')}
            onChange={(e) => void salvar({ ...g, config: { ...cfg, evento: e.target.value } })}
            className={campoClasse}
          >
            {EVENTOS.map((ev) => (
              <option key={ev} value={ev}>{ev}</option>
            ))}
          </select>
        </div>
      );
    }

    if (g.tipo === 'sem_resposta') {
      return (
        <div>
          <label className={rotuloClasse}>Minutos parada</label>
          <input
            type="number"
            min={5}
            defaultValue={Number(cfg.minutos ?? 60)}
            onBlur={(e) => void salvar({ ...g, config: { ...cfg, minutos: Number(e.target.value) } })}
            className={campoClasse}
          />
          <p className="mt-1 text-[11px] leading-snug text-gray-400 dark:text-slate-500">
            Conta desde a última mensagem do cliente, e só quando ainda não respondemos. Dispara
            uma vez por conversa.
          </p>
        </div>
      );
    }

    return null;
  };

  return (
    <aside className="flex h-full flex-col overflow-hidden rounded-2xl bg-white ring-1 ring-gray-200 dark:bg-slate-800 dark:ring-slate-700">
      <header className="flex shrink-0 items-start justify-between gap-2 border-b border-gray-100 px-4 py-3 dark:border-slate-700">
        <div>
          <p className="flex items-center gap-1.5 text-sm font-bold text-gray-800 dark:text-white">
            <Zap size={14} className="text-secondary" />
            Gatilhos
          </p>
          <p className="mt-0.5 text-[11px] leading-snug text-gray-500 dark:text-slate-400">
            Quando este fluxo começa sozinho.
          </p>
        </div>
        <button
          onClick={onFechar}
          className="rounded-lg p-1 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600 dark:hover:bg-slate-700"
        >
          <X size={15} />
        </button>
      </header>

      <div className="flex-1 space-y-3 overflow-y-auto px-4 py-3">
        {erro && (
          <p className="flex items-start gap-1.5 rounded-lg bg-red-50 px-2.5 py-1.5 text-[11px] text-red-700 dark:bg-red-900/20 dark:text-red-300">
            <AlertTriangle size={12} className="mt-0.5 shrink-0" />
            {erro}
          </p>
        )}

        {carregando ? (
          <div className="flex justify-center py-6">
            <Loader2 size={16} className="animate-spin text-gray-300" />
          </div>
        ) : (
          <>
            {gatilhos.map((g) => (
              <div key={g.id} className="rounded-xl bg-gray-50 p-2.5 dark:bg-slate-700/40">
                <div className="mb-1.5 flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <p className="text-xs font-semibold text-gray-800 dark:text-slate-100">
                      {ROTULO_GATILHO[g.tipo].titulo}
                    </p>
                    <p className="text-[11px] leading-snug text-gray-500 dark:text-slate-400">
                      {ROTULO_GATILHO[g.tipo].resumo}
                    </p>
                  </div>
                  <button
                    onClick={() => void remover(g.id)}
                    disabled={salvandoId === g.id}
                    className="shrink-0 rounded-md p-1 text-gray-300 transition-colors hover:bg-red-50 hover:text-red-500 dark:hover:bg-red-900/20"
                  >
                    <Trash2 size={12} />
                  </button>
                </div>

                {editarConfig(g)}

                <div className="mt-2 flex items-end gap-2">
                  <div className="w-20">
                    <label className={rotuloClasse}>Prioridade</label>
                    <input
                      type="number"
                      defaultValue={g.prioridade}
                      onBlur={(e) => void salvar({ ...g, prioridade: Number(e.target.value) })}
                      className={campoClasse}
                    />
                  </div>
                  <label className="flex items-center gap-1.5 pb-1.5 text-[11px] font-medium text-gray-600 dark:text-slate-300">
                    <input
                      type="checkbox"
                      checked={g.ativo}
                      onChange={(e) => void salvar({ ...g, ativo: e.target.checked })}
                      className="rounded accent-[#5F6C37]"
                    />
                    ativo
                  </label>
                </div>
              </div>
            ))}

            <div>
              <label className={rotuloClasse}>Adicionar gatilho</label>
              <select
                value=""
                disabled={salvandoId === 'novo'}
                onChange={(e) => e.target.value && void adicionar(e.target.value as TipoGatilho)}
                className={campoClasse}
              >
                <option value="">Escolha o tipo…</option>
                {(Object.keys(ROTULO_GATILHO) as TipoGatilho[]).map((t) => (
                  <option key={t} value={t}>{ROTULO_GATILHO[t].titulo}</option>
                ))}
              </select>
            </div>

            {/* ─── A fila da organização ─────────────────────────────────── */}
            <div className="border-t border-gray-100 pt-3 dark:border-slate-700">
              <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-500 dark:text-slate-400">
                Ordem de disparo na empresa
              </p>
              <p className="mb-2 mt-0.5 text-[11px] leading-snug text-gray-400 dark:text-slate-500">
                {PRECEDENCIA}
              </p>

              {fila.length === 0 ? (
                <p className="text-[11px] text-gray-400 dark:text-slate-500">Nenhum gatilho cadastrado ainda.</p>
              ) : (
                <ol className="space-y-1">
                  {fila.map((g, i) => {
                    const desteFluxo = g.flow_id === flowId;
                    const valendo = g.ativo && g.flow_ativo;
                    return (
                      <li
                        key={g.id}
                        className={`flex items-center gap-1.5 rounded-lg px-2 py-1 text-[11px] ${
                          desteFluxo
                            ? 'bg-primary/10 font-semibold text-primary'
                            : 'text-gray-600 dark:text-slate-300'
                        } ${valendo ? '' : 'opacity-40'}`}
                      >
                        <span className="w-4 shrink-0 text-center font-bold">{i + 1}º</span>
                        <span className="min-w-0 flex-1 truncate">
                          {g.flow_nome} · {ROTULO_GATILHO[g.tipo].titulo}
                        </span>
                        <span className="shrink-0 rounded bg-white px-1 font-mono text-[10px] text-gray-500 ring-1 ring-gray-200 dark:bg-slate-800 dark:ring-slate-600">
                          {g.prioridade}
                        </span>
                        {i < fila.length - 1 && <ArrowDown size={9} className="shrink-0 opacity-40" />}
                      </li>
                    );
                  })}
                </ol>
              )}
              <p className="mt-1.5 text-[11px] leading-snug text-gray-400 dark:text-slate-500">
                Apagado = gatilho desligado, ou fluxo desligado. Ele está na lista, mas não dispara.
              </p>
            </div>
          </>
        )}
      </div>

      <footer className="shrink-0 border-t border-gray-100 px-4 py-2.5 dark:border-slate-700">
        <p className="flex items-start gap-1.5 text-[11px] leading-snug text-gray-500 dark:text-slate-400">
          <Plus size={11} className="mt-0.5 shrink-0" />
          O gatilho só vale com o fluxo <strong>publicado e ligado</strong>. Fluxo em rascunho não
          dispara, mesmo com gatilho ativo.
        </p>
      </footer>
    </aside>
  );
}
